import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/core/hooks";
import { initApp, setBrandId, setPointId } from "@/core/app/reducer";
import { fetchBrand } from "@/core/brand/reducer";
import { fetchPoint } from "@/core/point/reducer";

interface DataLoaderProps {
  children: React.ReactNode;
}

// Temporary placeholder components
const LoadingContainer: React.FC<{ message: string }> = ({ message }) => (
  <div style={{ padding: "20px", textAlign: "center" }}>
    <div>Loading...</div>
    <div>{message}</div>
  </div>
);

const ErrorContainer: React.FC<{
  title: string;
  message: string;
  action?: { label: string; onClick: () => void };
}> = ({ title, message, action }) => (
  <div style={{ padding: "20px", textAlign: "center", color: "red" }}>
    <h3>{title}</h3>
    <p>{message}</p>
    {action && (
      <button
        onClick={action.onClick}
        style={{ padding: "10px 20px", marginTop: "10px" }}
      >
        {action.label}
      </button>
    )}
  </div>
);

export const DataLoader: React.FC<DataLoaderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  // Get URL parameters
  const urlBrandId = searchParams.get("brandId");
  const urlPointId = searchParams.get("pointId");

  // Get Redux state (including persisted values)
  const {
    initialized,
    brandId: storeBrandId,
    pointId: storePointId,
  } = useAppSelector((state) => state.app);
  const brandState = useAppSelector((state) => state.brand);
  const pointState = useAppSelector((state) => state.point);

  // Determine effective parameters with priority: URL > persist store > null
  const effectiveBrandId = urlBrandId || storeBrandId;
  const effectivePointId = urlPointId || storePointId;

  // Initialize app with parameters (priority logic implementation)
  useEffect(() => {
    if (effectiveBrandId && effectivePointId) {
      // If URL params exist and differ from store, update store
      if (urlBrandId) {
        dispatch(setBrandId(urlBrandId));
      }
      if (urlPointId) {
        dispatch(setPointId(urlPointId));
      }

      // Initialize app if not already initialized
      if (!initialized) {
        dispatch(initApp());

        // Fetch restaurant data
        dispatch(fetchBrand({ id: effectiveBrandId }));
        dispatch(
          fetchPoint({ id: effectivePointId, brandId: effectiveBrandId })
        );
      }
    }
  }, [
    effectiveBrandId,
    effectivePointId,
    urlBrandId,
    urlPointId,
    storeBrandId,
    storePointId,
    initialized,
    dispatch,
  ]);

  // Show error if no parameters available from any source
  if (!effectiveBrandId || !effectivePointId) {
    return (
      <ErrorContainer
        title="Restaurant Parameters Missing"
        message="Please provide brandId and pointId URL parameters to access the restaurant."
        action={{
          label: "Go to Restaurant Selection",
          onClick: () => (window.location.href = "/select-restaurant"),
        }}
      />
    );
  }

  // Show loading while fetching data
  if (!initialized || brandState.loading || pointState.loading) {
    const source =
      urlBrandId && urlPointId ? "URL parameters" : "stored preferences";
    return (
      <LoadingContainer message={`Loading restaurant data from ${source}...`} />
    );
  }

  // Show error if data failed to load
  if (brandState.error || pointState.error) {
    return (
      <ErrorContainer
        title="Failed to Load Restaurant"
        message={
          brandState.error || pointState.error || "Unknown error occurred"
        }
        action={{
          label: "Retry",
          onClick: () => {
            if (effectiveBrandId && effectivePointId) {
              dispatch(fetchBrand({ id: effectiveBrandId }));
              dispatch(
                fetchPoint({ id: effectivePointId, brandId: effectiveBrandId })
              );
            }
          },
        }}
      />
    );
  }

  // Render children if everything is loaded successfully
  return <>{children}</>;
};
